import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, FileText, Users, Mail, Calendar, Headphones, MessageSquare,
  Image, TrendingUp, Eye, Heart, MessageCircle, ArrowRight, Activity,
  Globe, Monitor, Smartphone, Tablet, Chrome, MapPin, Link2, Clock,
  ShieldAlert, Zap, RefreshCw, Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Country code → flag emoji */
const flag = (code) => {
  if (!code || code.length !== 2) return '🌐';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E0 + c.charCodeAt(0) - 65));
};

/* Relative time helper */
const relTime = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/* Period → numeric days (for timeseries endpoint) */
const PERIOD_DAYS = { today: 1, '24h': 1, '48h': 2, '7d': 7, '14d': 14, '30d': 30, '90d': 90 };

/* Tooltip wrapper */
const Tip = ({ text, children }) => (
  <div className="relative group/tip inline-block w-full">
    {children}
    {text && (
      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/tip:block z-30 pointer-events-none w-52">
        <div className="bg-[#0a1628] border border-gold/20 rounded-lg px-3 py-2 text-[9px] text-cream/55 leading-relaxed shadow-xl">
          {text}
        </div>
        <div className="w-2 h-2 bg-[#0a1628] border-r border-b border-gold/20 rotate-45 mx-auto -mt-1" />
      </div>
    )}
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub, color, link, tooltip }) => {
  const Wrapper = link ? Link : 'div';
  const card = (
    <Wrapper
      to={link || '#'}
      className={`bg-navy border border-gold/[0.06] rounded-xl p-4 ${link ? 'hover:border-gold/15 transition-all group cursor-pointer' : ''}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon size={16} className={color} />
        {link && <ArrowRight size={10} className="text-cream/10 group-hover:text-gold/30 transition-colors" />}
        {tooltip && !link && <Info size={10} className="text-cream/15 group-hover/tip:text-cream/30 transition-colors" />}
      </div>
      <p className="text-cream text-2xl font-semibold">{value}</p>
      <p className="text-cream/25 text-xs mt-0.5">{label}</p>
      {sub && <p className="text-cream/15 text-[10px] mt-0.5">{sub}</p>}
    </Wrapper>
  );
  return tooltip ? <Tip text={tooltip}>{card}</Tip> : card;
};

/* Mini bar chart — pure CSS */
const MiniChart = ({ data, dataKey, color, label }) => {
  const maxVal = Math.max(...data.map(d => d[dataKey]), 1);
  return (
    <div className="bg-navy border border-gold/[0.06] rounded-xl p-5" data-testid={`chart-${dataKey}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-cream/50 text-xs font-medium">{label}</h3>
        <span className="text-cream/20 text-[10px]">Last {data.length} days</span>
      </div>
      <div className="flex items-end gap-[2px] h-24">
        {data.map((d, i) => {
          const h = maxVal > 0 ? (d[dataKey] / maxVal) * 100 : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
              <div
                className={`w-full rounded-t-sm ${color} transition-all duration-200 group-hover:opacity-80`}
                style={{ height: `${Math.max(h, 2)}%`, minHeight: d[dataKey] > 0 ? '4px' : '1px', opacity: d[dataKey] > 0 ? 1 : 0.15 }}
              />
              <div className="absolute bottom-full mb-1 hidden group-hover:block z-10 pointer-events-none">
                <div className="bg-navy-light border border-gold/10 rounded px-2 py-1 text-[9px] text-cream/60 whitespace-nowrap shadow-lg">
                  {d.date?.slice(5)} : {d[dataKey]}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-cream/10 text-[9px]">{data[0]?.date?.slice(5)}</span>
        <span className="text-cream/10 text-[9px]">{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
      <div className="mt-3 pt-3 border-t border-gold/[0.04]">
        <span className="text-cream/40 text-xs font-medium">{data.reduce((s, d) => s + d[dataKey], 0)}</span>
        <span className="text-cream/15 text-[10px] ml-1">total this period</span>
      </div>
    </div>
  );
};

const AdminAnalytics = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [visits, setVisits] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const days = PERIOD_DAYS[period] || 30;
      const [statsRes, tsRes, visitsRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/cms/analytics/timeseries?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/cms/analytics/visits?period=${period}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (statsRes.ok) setData(await statsRes.json());
      if (tsRes.ok) setTimeseries(await tsRes.json());
      if (visitsRes.ok) setVisits(await visitsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/analytics/audit-log?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAuditLog(await res.json());
    } catch (e) { console.error(e); }
    setAuditLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchAuditLog(); }, [fetchAuditLog]);

  if (loading) return <div className="py-20 text-center text-cream/20">Loading analytics...</div>;
  if (!data) return <div className="py-20 text-center text-cream/30">Failed to load analytics.</div>;

  const series = timeseries?.series || [];
  const isNewSystem = visits?.data_source === 'analytics_events';

  const PERIODS = [
    { key: 'today', label: 'Today' },
    { key: '24h',   label: '24h' },
    { key: '48h',   label: '48h' },
    { key: '7d',    label: '7d' },
    { key: '14d',   label: '14d' },
    { key: '30d',   label: '30d' },
    { key: '90d',   label: '90d' },
  ];

  return (
    <div data-testid="admin-analytics">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Analytics</h1>
          <p className="text-cream/30 text-sm mt-1">
            Platform performance &amp; trends
            {isNewSystem && (
              <span className="ml-2 text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/15 rounded px-1.5 py-0.5">
                Bot-filtered
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-1 bg-navy border border-gold/[0.06] rounded-lg p-1">
          {PERIODS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              data-testid={`period-${key}`}
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
                period === key ? 'bg-gold/[0.1] text-gold' : 'text-cream/25 hover:text-cream/40'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Performance */}
      <div className="mb-8">
        <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
          <BarChart3 size={12} /> Content Performance
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Globe} label="Site Visits" color="text-teal-400"
            value={(data.site_visits?.total || 0).toLocaleString()}
            sub={`${(data.site_visits?.today_clean ?? data.site_visits?.today ?? 0).toLocaleString()} today`}
            tooltip="Total page-view events recorded in the legacy system. Use Unique Visitors for a more accurate human count."
          />
          <StatCard
            icon={Eye} label="Article Views" color="text-emerald-400"
            value={(data.site_visits?.article_views ?? data.content.total_views).toLocaleString()}
            sub={data.site_visits?.article_views != null ? 'bot-filtered, deduped' : 'cumulative'}
            tooltip="Deduplicated article view events from the new analytics system — each visitor counted once per article per session."
          />
          <StatCard
            icon={Users} label="Unique Visitors" color="text-sky-400"
            value={(data.site_visits?.unique_visitors || 0).toLocaleString()}
            sub="by visitor ID"
            tooltip="Distinct anonymous visitor IDs seen since the new analytics system launched. Each browser gets one persistent ID."
          />
          <StatCard
            icon={ShieldAlert} label="Bot Filtered" color="text-amber-400"
            value={(data.site_visits?.bot_filtered || 0).toLocaleString()}
            sub="excluded from metrics"
            tooltip="Requests matched against 30+ bot/crawler UA patterns and excluded from all human-traffic metrics."
          />
        </div>
      </div>

      {/* Engagement row */}
      <div className="mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Eye}           label="Cumul. Article Views" value={data.content.total_views.toLocaleString()} color="text-emerald-400/60"
            tooltip="Lifetime article view counter incremented on each article fetch. Includes historical data before the new analytics system." />
          <StatCard icon={Heart}          label="Total Likes"          value={data.content.total_likes}                 color="text-red-400" />
          <StatCard icon={MessageCircle}  label="Total Comments"       value={data.content.total_comments}              color="text-purple-400" />
          <StatCard icon={FileText}       label="Published Articles"   value={data.content.articles_published}          color="text-gold"
            tooltip="Count of articles with status='published' in the CMS." link="/admin/articles" />
        </div>
      </div>

      {/* Time Series Charts */}
      {series.length > 0 && (
        <div className="mb-8">
          <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
            <Activity size={12} /> Trends
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            <MiniChart data={series} dataKey="page_visits" color="bg-teal-400"  label="Site Visits" />
            <MiniChart data={series} dataKey="signups"     color="bg-blue-400"  label="User Signups" />
            <MiniChart data={series} dataKey="subscribers" color="bg-amber-400" label="New Subscribers" />
          </div>
        </div>
      )}

      {/* Top Articles */}
      {data.content.top_articles.length > 0 && (
        <div className="mb-8">
          <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
            <TrendingUp size={12} /> Top Articles
          </h2>
          <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gold/[0.04]">
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">#</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">Article</th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">Views</th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">Likes</th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15 hidden sm:table-cell">Comments</th>
                  <th className="px-4 py-2.5 text-[10px] text-cream/15 hidden md:table-cell w-24">Trend</th>
                </tr>
              </thead>
              <tbody>
                {data.content.top_articles.map((a, i) => (
                  <tr key={i} className="border-b border-gold/[0.03] hover:bg-cream/[0.01]">
                    <td className="px-4 py-2.5 text-cream/15 text-xs font-mono">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <Link to={`/articles/${a.slug}`} className="text-cream/60 hover:text-gold transition-colors text-xs">
                        {a.title}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-right text-cream/30 text-xs">{a.views}</td>
                    <td className="px-4 py-2.5 text-right text-cream/30 text-xs">{a.likes}</td>
                    <td className="px-4 py-2.5 text-right text-cream/30 text-xs hidden sm:table-cell">{a.comments}</td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <div className="h-1.5 bg-cream/[0.03] rounded-full overflow-hidden w-20">
                        <div
                          className="h-full bg-gold/40 rounded-full"
                          style={{ width: `${data.content.total_views > 0 ? (a.views / data.content.total_views) * 100 : 0}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Content Timeline */}
      {timeseries?.content_timeline?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
            <Calendar size={12} /> Content Timeline
          </h2>
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
            <div className="space-y-2">
              {timeseries.content_timeline.map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="text-cream/15 w-16 flex-shrink-0 font-mono">{item.date}</span>
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.status === 'published' ? 'bg-green-400' : 'bg-cream/20'}`} />
                  <span className="text-cream/40 flex-1 truncate">{item.title}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${item.status === 'published' ? 'bg-green-500/10 text-green-400' : 'bg-cream/[0.03] text-cream/20'}`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Audience & Community */}
      <div className="mb-8">
        <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
          <Users size={12} /> Audience &amp; Community
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users}    label="Total Users"         value={data.users.total}          sub={`${data.users.verified} verified`}           color="text-blue-400"   link="/admin/users"    tooltip="Registered reader accounts." />
          <StatCard icon={Mail}     label="Active Subscribers"  value={data.subscribers.active}   sub={`${data.subscribers.unsubscribed} unsub`}     color="text-amber-400"  link="/admin/mailing"  tooltip="Cultural Fluency mailing list subscribers who haven't unsubscribed." />
          <StatCard icon={Calendar} label="Events"              value={data.events.upcoming}       sub={`${data.events.past} past`}                   color="text-emerald-400" link="/admin/events" />
          <StatCard icon={Image}    label="Media Assets"        value={data.media.total}                                                             color="text-pink-400"   link="/admin/media" />
        </div>
      </div>

      {/* Publishing */}
      <div className="mb-8">
        <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
          <MessageSquare size={12} /> Publishing &amp; Distribution
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Headphones}    label="Podcast Episodes" value={data.podcast.published}                              color="text-purple-400" link="/admin/podcast" />
          <StatCard icon={MessageSquare} label="Social Posts"     value={data.social.published}  sub={`${data.social.drafts} drafts`} color="text-cyan-400"   link="/admin/social" />
          <StatCard icon={Mail}          label="Newsletters Sent" value={data.newsletters.sent}                               color="text-orange-400" link="/admin/mailing" />
          <StatCard icon={Users}         label="Blocked Users"    value={data.users.blocked}                                  color="text-red-400" />
        </div>
      </div>

      {/* ── Visitor Intelligence ── */}
      {visits && (
        <div className="mb-8">
          <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
            <MapPin size={12} /> Visitor Intelligence
            {visits.data_source === 'analytics_events' && (
              <span className="ml-1 text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/15 rounded px-1.5 py-0.5">
                New system
              </span>
            )}
            {visits.data_source === 'legacy' && (
              <span className="ml-1 text-[9px] bg-amber-500/10 text-amber-400 border border-amber-500/15 rounded px-1.5 py-0.5">
                Legacy data
              </span>
            )}
          </h2>

          {/* Period summary row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-1">Total Events</p>
              <p className="text-cream text-xl font-semibold">{(visits.total || 0).toLocaleString()}</p>
            </div>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-1">Unique Visitors</p>
              <p className="text-cream text-xl font-semibold">{(visits.unique_visitors || 0).toLocaleString()}</p>
              {visits.unique_sessions != null && (
                <p className="text-cream/20 text-[10px] mt-0.5">{visits.unique_sessions} sessions</p>
              )}
            </div>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-1">Bots Filtered</p>
              <p className="text-amber-400 text-xl font-semibold">{(visits.bot_filtered || 0).toLocaleString()}</p>
            </div>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-1">Data Source</p>
              <p className={`text-sm font-medium ${visits.data_source === 'analytics_events' ? 'text-emerald-400' : 'text-amber-400'}`}>
                {visits.data_source === 'analytics_events' ? 'New (clean)' : 'Legacy'}
              </p>
            </div>
          </div>

          {visits.total === 0 ? (
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-8 text-center text-cream/20 text-sm">
              No visitor data yet for this period. Data populates as pages are visited.
            </div>
          ) : (
            <div className="space-y-4">

              {/* Row 1 — Devices + Browsers + Referrers */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* Devices */}
                <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
                  <p className="text-cream/30 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Monitor size={10} /> Devices
                  </p>
                  <div className="space-y-2">
                    {[['desktop','Desktop',Monitor],['mobile','Mobile',Smartphone],['tablet','Tablet',Tablet]].map(([key, label, Icon]) => {
                      const count = visits.devices[key] || 0;
                      const pct   = visits.total > 0 ? Math.round((count / visits.total) * 100) : 0;
                      return (
                        <div key={key}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-cream/50 flex items-center gap-1"><Icon size={10} />{label}</span>
                            <span className="text-cream/30">{count} <span className="text-cream/15">({pct}%)</span></span>
                          </div>
                          <div className="h-1 bg-cream/[0.04] rounded-full overflow-hidden">
                            <div className="h-full bg-teal-400/60 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Browsers */}
                <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
                  <p className="text-cream/30 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Chrome size={10} /> Browsers
                  </p>
                  <div className="space-y-2">
                    {visits.browsers.slice(0, 5).map(({ browser, count }) => {
                      const pct = visits.total > 0 ? Math.round((count / visits.total) * 100) : 0;
                      return (
                        <div key={browser}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-cream/50">{browser}</span>
                            <span className="text-cream/30">{count} <span className="text-cream/15">({pct}%)</span></span>
                          </div>
                          <div className="h-1 bg-cream/[0.04] rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400/60 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Referrers */}
                <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
                  <p className="text-cream/30 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Link2 size={10} /> Traffic Sources
                  </p>
                  <div className="space-y-2">
                    {visits.referrers.slice(0, 5).map(({ source, count }) => {
                      const pct = visits.total > 0 ? Math.round((count / visits.total) * 100) : 0;
                      return (
                        <div key={source}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-cream/50 truncate max-w-[120px]">{source}</span>
                            <span className="text-cream/30">{count} <span className="text-cream/15">({pct}%)</span></span>
                          </div>
                          <div className="h-1 bg-cream/[0.04] rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400/60 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Row 2 — UTM Traffic (new system only) */}
              {visits.utm_traffic?.length > 0 && (
                <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
                  <p className="text-cream/30 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Link2 size={10} /> UTM Campaign Traffic
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {visits.utm_traffic.map((u, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gold/[0.03] last:border-0">
                        <div>
                          <span className="text-cream/60">{u.source}</span>
                          {u.medium && <span className="text-cream/25 ml-1">/ {u.medium}</span>}
                          {u.campaign && <span className="text-cream/15 ml-1 truncate">— {u.campaign}</span>}
                        </div>
                        <span className="text-cream/35 text-xs ml-2 flex-shrink-0">{u.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Row 3 — Countries + Top Pages */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Countries */}
                <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gold/[0.04]">
                    <p className="text-cream/30 text-[10px] uppercase tracking-wider flex items-center gap-1.5">
                      <Globe size={10} /> Visitors by Country
                    </p>
                  </div>
                  <div className="divide-y divide-gold/[0.03]">
                    {visits.countries.slice(0, 8).map(({ country, code, count }, i) => {
                      const maxCount = visits.countries[0]?.count || 1;
                      const pct = Math.round((count / maxCount) * 100);
                      return (
                        <div key={code || i} className="px-4 py-2.5 flex items-center gap-3 hover:bg-cream/[0.01]">
                          <span className="text-lg leading-none flex-shrink-0">{flag(code)}</span>
                          <span className="text-cream/60 text-xs flex-1 truncate">{country}</span>
                          <div className="w-16 h-1 bg-cream/[0.04] rounded-full overflow-hidden hidden sm:block">
                            <div className="h-full bg-gold/40 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-cream/25 text-xs tabular-nums w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top Pages */}
                <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gold/[0.04]">
                    <p className="text-cream/30 text-[10px] uppercase tracking-wider flex items-center gap-1.5">
                      <TrendingUp size={10} /> Top Pages
                    </p>
                  </div>
                  <div className="divide-y divide-gold/[0.03]">
                    {visits.top_pages.map(({ path: p, count }, i) => {
                      const maxCount = visits.top_pages[0]?.count || 1;
                      const pct = Math.round((count / maxCount) * 100);
                      const label = p === '/' ? 'Home' : p.replace('/articles/', '').replace(/-/g, ' ');
                      return (
                        <div key={p} className="px-4 py-2.5 flex items-center gap-3 hover:bg-cream/[0.01]">
                          <span className="text-cream/15 text-[10px] font-mono w-4 flex-shrink-0">{i + 1}</span>
                          <span className="text-cream/50 text-xs flex-1 truncate" title={p}>{label}</span>
                          <div className="w-16 h-1 bg-cream/[0.04] rounded-full overflow-hidden hidden sm:block">
                            <div className="h-full bg-emerald-400/40 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-cream/25 text-xs tabular-nums w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Top Articles by Visits (new system only) */}
              {visits.top_articles_by_visits?.length > 0 && (
                <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gold/[0.04]">
                    <p className="text-cream/30 text-[10px] uppercase tracking-wider flex items-center gap-1.5">
                      <Eye size={10} /> Top Articles by Visits <span className="ml-1 text-emerald-400/50">(bot-filtered)</span>
                    </p>
                  </div>
                  <div className="divide-y divide-gold/[0.03]">
                    {visits.top_articles_by_visits.map(({ slug, count }, i) => {
                      const maxCount = visits.top_articles_by_visits[0]?.count || 1;
                      const pct = Math.round((count / maxCount) * 100);
                      return (
                        <div key={slug} className="px-4 py-2.5 flex items-center gap-3 hover:bg-cream/[0.01]">
                          <span className="text-cream/15 text-[10px] font-mono w-4 flex-shrink-0">{i + 1}</span>
                          <Link to={`/articles/${slug}`} className="text-cream/50 hover:text-gold text-xs flex-1 truncate transition-colors">
                            {slug?.replace(/-/g, ' ')}
                          </Link>
                          <div className="w-16 h-1 bg-cream/[0.04] rounded-full overflow-hidden hidden sm:block">
                            <div className="h-full bg-sky-400/40 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-cream/25 text-xs tabular-nums w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent Visits */}
              {visits.recent?.length > 0 && (
                <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gold/[0.04] flex items-center justify-between">
                    <p className="text-cream/30 text-[10px] uppercase tracking-wider flex items-center gap-1.5">
                      <Clock size={10} /> Recent Visits
                    </p>
                    <span className="text-cream/15 text-[9px]">Last {visits.recent.length}</span>
                  </div>
                  <div className="divide-y divide-gold/[0.03] max-h-64 overflow-y-auto">
                    {visits.recent.map((v, i) => (
                      <div key={i} className="px-4 py-2 flex items-center gap-3 text-xs hover:bg-cream/[0.01]">
                        <span className="text-lg leading-none flex-shrink-0">{flag(v.country_code)}</span>
                        <span className="text-cream/40 flex-1 truncate" title={v.path}>
                          {v.path === '/' ? 'Home' : v.path}
                        </span>
                        {v.event_type === 'article_view' && (
                          <span className="text-[9px] bg-sky-500/10 text-sky-400 border border-sky-500/15 rounded px-1 hidden sm:inline">article</span>
                        )}
                        <span className="text-cream/20 hidden sm:inline">{v.device}</span>
                        <span className="text-cream/20 hidden md:inline">{v.browser}</span>
                        <span className="text-cream/15 text-[10px] hidden lg:inline tabular-nums">
                          {v.timestamp ? new Date(v.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Real-time Audit Log ── */}
      <div className="mb-8" data-testid="audit-log-section">
        <h2 className="text-cream/40 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
          <Zap size={12} /> Real-time Audit Log
          <span className="text-cream/15 text-[9px] font-normal normal-case ml-1">— all events including bots &amp; filtered</span>
          <button
            onClick={fetchAuditLog}
            disabled={auditLoading}
            data-testid="audit-log-refresh"
            className="ml-auto flex items-center gap-1 text-cream/20 hover:text-cream/40 transition-colors"
          >
            <RefreshCw size={10} className={auditLoading ? 'animate-spin' : ''} />
            <span className="text-[9px]">Refresh</span>
          </button>
        </h2>

        {auditLog ? (
          <>
            {/* Summary pills */}
            <div className="flex gap-2 mb-3 flex-wrap">
              <span className="text-[9px] bg-cream/[0.03] text-cream/30 rounded px-2 py-1 border border-gold/[0.05]">
                {(auditLog.total_logged || 0).toLocaleString()} total logged
              </span>
              <span className="text-[9px] bg-red-500/10 text-red-400 rounded px-2 py-1 border border-red-500/15">
                {(auditLog.bots_total || 0).toLocaleString()} bots
              </span>
              <span className="text-[9px] bg-amber-500/10 text-amber-400 rounded px-2 py-1 border border-amber-500/15">
                {(auditLog.filtered_total || 0).toLocaleString()} filtered
              </span>
            </div>

            {auditLog.entries?.length === 0 ? (
              <div className="bg-navy border border-gold/[0.06] rounded-xl p-8 text-center text-cream/20 text-sm">
                No audit log entries yet. Events populate as pages are visited.
              </div>
            ) : (
              <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-navy z-10">
                      <tr className="border-b border-gold/[0.04]">
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15 w-20">When</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15">Path</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15 hidden sm:table-cell">Visitor</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15 hidden md:table-cell">Country</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15 hidden md:table-cell">Device</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15 hidden lg:table-cell">Referrer</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-cream/15">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.entries.map((e, i) => (
                        <tr
                          key={e.id || i}
                          data-testid={`audit-row-${i}`}
                          className={`border-b border-gold/[0.03] hover:bg-cream/[0.01] ${e.filtered ? 'opacity-50' : ''}`}
                        >
                          <td className="px-3 py-2 text-cream/20 whitespace-nowrap tabular-nums">{relTime(e.timestamp)}</td>
                          <td className="px-3 py-2 text-cream/50 truncate max-w-[160px]" title={e.path}>
                            {e.path === '/' ? 'Home' : e.path}
                            {e.event_type === 'article_view' && (
                              <span className="ml-1 text-[8px] text-sky-400/70">art</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-cream/25 font-mono hidden sm:table-cell">{e.visitor_prefix || '—'}</td>
                          <td className="px-3 py-2 text-cream/25 hidden md:table-cell">{e.country || '—'}</td>
                          <td className="px-3 py-2 text-cream/25 hidden md:table-cell">{e.device || '—'}</td>
                          <td className="px-3 py-2 text-cream/20 hidden lg:table-cell truncate max-w-[100px]">{e.referrer || '—'}</td>
                          <td className="px-3 py-2">
                            {e.is_bot ? (
                              <span className="text-[8px] bg-red-500/10 text-red-400 border border-red-500/20 rounded px-1.5 py-0.5">BOT</span>
                            ) : e.is_admin ? (
                              <span className="text-[8px] bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded px-1.5 py-0.5">ADMIN</span>
                            ) : e.filter_reason === 'duplicate_article_view' ? (
                              <span className="text-[8px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded px-1.5 py-0.5">DUP</span>
                            ) : (
                              <span className="text-[8px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded px-1.5 py-0.5">OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-8 text-center text-cream/20 text-sm">
            {auditLoading ? 'Loading audit log…' : 'Failed to load audit log.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
